import { useState, useEffect, useCallback } from "react";
import Form from "../../../form/Form";
import Input from "../../../form/Input";
import Button from "../../../form/Button";
import Select from "../../../form/select/Select";
import useForm from "../../../../hooks/useForm";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import {
  dynamicFieldAddEdit,
  dynamicFormAddEdit,
  dynamicFormDelete,
  dynamicFormDetails,
} from "../../../../services/dynamicFormService";
import Checkbox from "../../../form/Checkbox";
import Editor from "../../../elements/tiptap-editor/Editor";
import { logDOM } from "@testing-library/react";
import Radio from "../../../form/Radio";
import { t } from "i18next";
import LangSwitch from "../../../elements/LangSwitch";

// Constants moved from GlobalCheckoutForm
const STATIC_QUESTION_LABELS = [
  "first name|last name",
  "first name | last name",
  "full name",
  "email",
  "first name",
  "last name",
];

const AddBuyerQuestion = ({
  data = {},
  getResponse = () => {},
  close = () => {},
  formFor,
  crudPayload,
}) => {
  const { selectedOrganizationDetails } = useSelector(
    (state) => state?.organization
  );
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [currentOption, setCurrentOption] = useState("");
  const [staticLabelWarning, setStaticLabelWarning] = useState("");
  const queryClient = useQueryClient();
  const { lang } = useSelector((state) => state?.langProvider);
  const [selectedLang, setSelectedLang] = useState(lang || "en");

  const apply_to_options = [
    { label: t("All"), value: "all" },
    { label: t("Tickets"), value: "ticket" },
    { label: t("Addons"), value: "addons-event" },
  ];

  // Function to check if input contains static labels
  const checkForStaticLabels = useCallback((inputValue) => {
    if (!inputValue || typeof inputValue !== "string") return null;

    const normalizedInput = inputValue.toLowerCase().trim();

    // Check for exact matches or containing static labels
    const foundLabel = STATIC_QUESTION_LABELS.find((label) => {
      const normalizedLabel = label.toLowerCase();
      return (
        normalizedInput === normalizedLabel ||
        normalizedInput.includes(normalizedLabel) ||
        normalizedLabel.includes(normalizedInput)
      );
    });

    if (foundLabel) {
      return `Cannot use "${foundLabel}" as it's a reserved system question. Please choose a different question text.`;
    }

    return null;
  }, []);

  // ✅ Base validation rules that don't change
  const baseValidationRules = {
    questionText: [
      {
        required: true,
        message: "Question text is required",
        errorType: "required",
      },
      {
        type: "range",
        max: 200,
        message: "Question text cannot exceed 200 characters",
        errorType: "warning",
      },
      // Add custom validation for static labels
      {
        type: "custom",
        validator: (value) => {
          const warningMessage = checkForStaticLabels(value);
          return warningMessage
            ? { isValid: false, message: warningMessage }
            : { isValid: true };
        },
        errorType: "error",
      },
    ],
    questionType: {
      required: true,
      message: "Question type is required",
      errorType: "required",
    },
  };

  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    formValidate,
    updateErrors,
  } = useForm({
    validationRules: baseValidationRules,
    initialState: { ...(formFor === "attendee" ? { fieldFor: "all" } : {}) },
  });

  // Handle question text change with real-time validation
  const handleQuestionTextChange = useCallback(
    (e) => {
      const inputValue = e.target.value;

      // Call the original handleChange
      handleChange(e);

      // Check for static labels and set warning
      const warning = checkForStaticLabels(inputValue);
      setStaticLabelWarning(warning || "");
    },
    [handleChange, checkForStaticLabels]
  );

  // ✅ Manual validation for conditional fields
  const validateConditionalFields = () => {
    let customErrors = {};

    // Validate options for select/radio/checkbox types (but not terms - validated by content)
    if (
      ["select", "radio", "checkbox"].includes(values?.questionType) &&
      values?.questionType !== "terms" &&
      (!values?.optionsArray || values.optionsArray.length === 0)
    ) {
      customErrors.optionsArray = {
        message: "Please add at least one option for this question type",
        type: "required",
      };
    }

    // Validate terms and conditions content
    if (
      values?.questionType === "terms" &&
      (!values?.overView || values?.overView.trim() === "")
    ) {
      customErrors.overView = {
        message: "Please add terms and conditions content",
        type: "required",
      };
    }

    return customErrors;
  };

  const {
    data: details,
    error: eventError,
    isFetching,
    isLoading: formDetailsLoading,
  } = useQuery({
    queryKey: ["dynamicForm-details-data", data?.formId],
    queryFn: async () => {
      const res = await dynamicFormDetails({ id: data?.formId });
      return res;
    },
    enabled: !!data?.formId,
  });

  // Add mutation for removing options from existing fields
  const { mutate: removeOptionAPI } = useMutation({
    mutationFn: async (val) => {
      return await dynamicFormDelete(val);
    },
    onSuccess: (res) => {
      if (res?.status === 200) {
        queryClient.invalidateQueries([
          "dynamicForm-details-data",
          data?.formId,
        ]);
      } else {
        toast.error(res?.message || "Failed to remove option");
      }
    },
    onError: (error) => {
      console.error("Error removing option:", error);
      toast.error("Failed to remove option. Please try again.");
    },
  });

  useEffect(() => {
    return () => {
      queryClient.removeQueries({
        queryKey: ["dynamicForm-details-data", data?.formId],
      });
    };
  }, []);

  useEffect(() => {
    const isEditMode =
      details?.data?._id &&
      data?.isEdit &&
      details?.data?.sectionDetail?.[0]?.relatedField?.[0];

    if (isEditMode) {
      const fieldData = details.data.sectionDetail
        ?.find((item) => item?._id === data?.sectionId)
        ?.relatedField.find((field) => field._id === data?.id);

      // Determine if this is a terms field based on defaultValue containing HTML
      const isTermsField =
        fieldData?.defaultValue &&
        typeof fieldData?.defaultValue === "string" &&
        fieldData?.defaultValue.includes("<") &&
        fieldData?.defaultValue.includes(">");

      const questionText = fieldData?.label?.text?.[selectedLang] || "";
      setFieldsValue({
        ...(formFor === "attendee"
          ? { fieldFor: fieldData?.fieldFor || "all" }
          : {}),
        questionText: questionText,
        questionType: isTermsField ? "terms" : fieldData?.type || "input",
        isRequired: fieldData?.required || false,
        allowedInput: fieldData?.validation?.pattern || ".*",
        maxLength: fieldData?.validation?.maxLength || "",
        minLength: fieldData?.validation?.minLength || "",
        overView: isTermsField ? fieldData?.defaultValue : "",
        optionsArray: ((!isTermsField && fieldData?.options) || []).map(
          (option) => ({
            label: option.label?.text?.[selectedLang] || "",
            value: option.key || "",
            key: option.key,
            default: option.default || false,
          })
        ),
      });

      // Check for static labels in edit mode
      const warning = checkForStaticLabels(questionText);
      setStaticLabelWarning(warning || "");

      if (
        fieldData?.type === "input" &&
        fieldData?.validation &&
        (fieldData?.validation.minLength || fieldData?.validation.maxLength)
      ) {
        setShowAdvancedOptions(true);
      }
    } else {
      setFieldsValue({
        questionText: "",
        questionType: "input",
        isRequired: false,
        allowedInput: ".*",
        maxLength: "",
        minLength: "",
        overView: "",
        optionsArray: [],
        ...(formFor === "attendee" ? { fieldFor: "all" } : {}),
      });
      setShowAdvancedOptions(false);
      setStaticLabelWarning("");
    }
  }, [
    details?.data?._id,
    details?.data?.sectionDetail,
    checkForStaticLabels,
    selectedLang,
  ]);

  // ✅ Real-time validation for conditional fields
  useEffect(() => {
    const conditionalErrors = validateConditionalFields();

    // Only update errors for conditional fields
    const fieldsToUpdate = [
      "optionsArray",
      "overView",
      "minLength",
      "maxLength",
    ];
    const updatedErrors = {};

    fieldsToUpdate.forEach((field) => {
      if (conditionalErrors[field]) {
        updatedErrors[field] = conditionalErrors[field];
      } else {
        updatedErrors[field] = null; // Clear error
      }
    });

    updateErrors(updatedErrors);
  }, [
    values.questionType,
    values.optionsArray,
    values.overView,
    values.minLength,
    values.maxLength,
    showAdvancedOptions,
  ]);

  // FIXED: Updated question types to include terms as separate type
  const questionTypes = [
    { name: "Single line text", value: "input" },
    { name: "Multi line text", value: "textarea" },
    { name: "Select answer from drop-down", value: "select" },
    { name: "Select answer from radio buttons", value: "radio" },
    { name: "Select answer from checkboxes", value: "checkbox" },
    { name: "Terms and conditions agreement", value: "terms" }, // Separate type
    { name: "Enter a date", value: "date" },
  ];

  const allowedInputOptions = [
    {
      name: "Any characters",
      value: ".*", // matches anything
    },
    {
      name: "A-Za-z0-9 (no spaces)",
      value: "^[A-Za-z0-9]+$",
    },
    {
      name: "A-Za-z (no spaces)",
      value: "^[A-Za-z]+$",
    },
    {
      name: "0-9 (no spaces)",
      value: "^[0-9]+$",
    },
    {
      name: "A-Za-z0-9 (spaces allowed)",
      value: "^[A-Za-z0-9 ]+$",
    },
    {
      name: "A-Za-z (spaces allowed)",
      value: "^[A-Za-z ]+$",
    },
    {
      name: "0-9 (spaces allowed)",
      value: "^[0-9 ]+$",
    },
    {
      name: "At least two words of any characters, forcing at least one space, e.g. for a full name",
      value: "^(?=\\S+\\s+\\S+).+$", // At least two words
    },
    {
      name: "Email address",
      value: "^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}$",
    },
    {
      name: "URL",
      value: "^(https?:\\/\\/)?([\\w-]+\\.)+[\\w-]{2,}(\\/\\S*)?$",
    },
  ];

  const handleOptionKeyDown = (e) => {
    if (e.key !== "Enter" || !currentOption.trim()) return;
    e.preventDefault();

    const label = currentOption.trim();
    const value = label.toLowerCase().replace(/\s+/g, "_");
    const newOption = { label, value, default: false };

    const currentOptions = values?.optionsArray || [];

    const isDuplicate = currentOptions.some(
      (opt) => opt.label.toLowerCase() === label.toLowerCase()
    );

    if (isDuplicate) {
      toast.warning("Option already exists");
      return;
    }

    const updatedOptions = [...currentOptions, newOption];
    handleChange({
      target: { name: "optionsArray", value: updatedOptions },
    });

    setCurrentOption("");
  };

  // Enhanced removeOption function with API integration
  const removeOption = (index) => {
    const currentOptions = values?.optionsArray || [];
    const optionToRemove = currentOptions[index];

    // Check if this is an existing field being edited
    const isEditingExistingField = data?.isEdit && data?.id;

    if (isEditingExistingField && optionToRemove?.key) {
      // For existing options with keys, call API to remove
      const payload = {
        contentType: "fromOption",
        id: data.id, // field ID
        optionKey: optionToRemove.key, // option key to remove
      };
      removeOptionAPI(payload);
    }

    // Update local state regardless (optimistic update)
    const updatedOptions = currentOptions.toSpliced
      ? currentOptions.toSpliced(index, 1)
      : currentOptions.filter((_, i) => i !== index);

    handleChange({ target: { name: "optionsArray", value: updatedOptions } });
  };

  const handleQuestionTypeChange = (value) => {
    const updates = {
      questionType: value,
      isRequired: false,
    };

    // Auto-require terms fields
    if (value === "terms") {
      updates.isRequired = true;
      // Clear options array - will be auto-generated from editor content
      updates.optionsArray = [];
    }

    // Clear options for non-option types
    if (!["select", "radio", "checkbox"].includes(value)) {
      updates.optionsArray = [];
      setCurrentOption("");
    }

    // Reset advanced options for non-input types
    if (value === "input") {
      setShowAdvancedOptions(false);
    }

    setFieldsValue({
      ...values,
      ...updates,
    });

    // ✅ Clear errors when question type changes
    updateErrors({
      optionsArray: undefined,
      overView: undefined,
      minLength: undefined,
      maxLength: undefined,
    });
  };

  // ✅ Simplified custom validation for complex relationships
  const validateFormData = () => {
    const conditionalErrors = validateConditionalFields();

    // Check for static label warning
    if (staticLabelWarning) {
      updateErrors({
        ...conditionalErrors,
        questionText: {
          message: staticLabelWarning,
          type: "error",
        },
      });
      return false;
    }

    if (Object.keys(conditionalErrors).length > 0) {
      updateErrors(conditionalErrors);
      return false;
    }

    return true;
  };

  // Helper function to parse HTML content into options array
  const parseTermsHtmlToOptions = useCallback((htmlContent) => {
    if (!htmlContent || typeof htmlContent !== "string") {
      return [];
    }

    try {
      // Create a temporary DOM element to parse HTML
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = htmlContent;

      // Find all paragraphs, list items, and divs (for line breaks)
      const paragraphs = tempDiv.querySelectorAll("p");
      const listItems = tempDiv.querySelectorAll("li");
      const divs = tempDiv.querySelectorAll("div");
      const options = [];

      // Process paragraphs first (most common when pressing Enter)
      if (paragraphs.length > 0) {
        paragraphs.forEach((item, index) => {
          const text = item.textContent?.trim();
          if (text) {
            options.push({
              label: text,
              value: `option_${index}`,
              default: false,
            });
          }
        });
      }
      // Process list items if no paragraphs
      else if (listItems.length > 0) {
        listItems.forEach((item, index) => {
          const text = item.textContent?.trim();
          if (text) {
            options.push({
              label: text,
              value: `option_${index}`,
              default: false,
            });
          }
        });
      }
      // Process divs if no paragraphs or list items (fallback for some editors)
      else if (divs.length > 0) {
        divs.forEach((item, index) => {
          const text = item.textContent?.trim();
          if (text) {
            options.push({
              label: text,
              value: `option_${index}`,
              default: false,
            });
          }
        });
      }
      // Fallback: split by line breaks
      else {
        const lines = htmlContent
          .split(/\r?\n|\r/)
          .filter((line) => line.trim());
        lines.forEach((line, index) => {
          // Remove HTML tags for plain text
          const text = line.replace(/<[^>]*>/g, "").trim();
          if (text) {
            options.push({
              label: text,
              value: `option_${index}`,
              default: false,
            });
          }
        });
      }
      return options;
    } catch (error) {
      console.error("Error parsing HTML content:", error);
      return [];
    }
  }, []);

  // Also make sure your handleEditorChange function is properly updating optionsArray:
  const handleEditorChange = useCallback(
    (val) => {
      setFieldsValue({
        overView: val,
      });
      // Auto-parse HTML content to options array
      if (values?.questionType === "terms") {
        const parsedOptions = parseTermsHtmlToOptions(val);
        if (parsedOptions.length > 0) {
          setFieldsValue({
            optionsArray: parsedOptions,
          });
        }
      }
    },
    [values?.questionType, parseTermsHtmlToOptions, handleChange]
  );

  const { mutate: onSubmit, isPending: isLoading } = useMutation({
    mutationFn: async (val) => {
      const fieldData = details.data.sectionDetail
        ?.find((item) => item?._id === data?.sectionId)
        ?.relatedField.find((field) => field._id === data?.id);

      const baseField = {
        fieldFor: val?.fieldFor,
        label: {
          text: {
            ...fieldData?.label?.text,
            [selectedLang]: val?.questionText,
          },
        },
        type: val?.questionType === "terms" ? "checkbox" : val?.questionType, // Save terms as checkbox
        required: val?.isRequired || false,
      };

      // FIXED: Handle terms field - include BOTH defaultValue AND options
      if (val?.questionType === "terms") {
        // Store HTML content in defaultValue (for editing and display)
        baseField.defaultValue = val?.overView;

        // IMPORTANT: Also include options array (for checkbox processing)
        if (val?.optionsArray?.length > 0) {
          baseField.options = val.optionsArray.map((option, i) => ({
            label: {
              text: {
                ...fieldData?.options?.[i]?.label?.text,
                [selectedLang]: option?.label,
              },
            },
            default: false,
          }));
        }
      }

      // Handle advanced options for input fields
      if (showAdvancedOptions && val?.questionType === "input") {
        baseField.validation = {
          minLength: val?.minLength,
          maxLength: val?.maxLength,
          pattern: val?.allowedInput,
        };
      }

      // Handle options for select, radio, checkbox (but not terms)
      if (
        ["select", "radio", "checkbox"].includes(val?.questionType) &&
        val?.questionType !== "terms" &&
        val?.optionsArray?.length > 0
      ) {
        baseField.options = val.optionsArray.map((option, i) => ({
          label: {
            text: {
              ...fieldData?.options?.[i]?.label?.text,
              [selectedLang]: option?.label,
            },
          },
          key: option?.value,
          default: false,
        }));
      }

      if (data?.id && data?.isEdit) {
        baseField.id = data?.id;
      }

      const intial_add_data = {
        title: {
          [selectedLang]: data?.isEdit
            ? "Edit Custom Question"
            : "Add Custom Question",
        },
        description: {
          [selectedLang]: data?.isEdit
            ? "Edit the custom question that will be asked during checkout."
            : "Create a custom question that will be asked during checkout.",
        },
      };

      const payload = {
        title: {
          [selectedLang]: data?.isEdit
            ? "Edit buyer question"
            : "Add buyer only question",
        },
        description: {
          [selectedLang]: data?.isEdit
            ? "Edit the existing question that will be asked to customers when they make an order."
            : "Create a new question that will be asked to customers when they make an order. You can customize the question type, label, and other settings.",
        },
        section: [
          {
            ...intial_add_data,
            field: formFor === "attendee" ? [] : [baseField],
            sectionFor: "billing",
          },
          {
            ...intial_add_data,
            field: formFor === "attendee" ? [baseField] : [],
            sectionFor: "attendee",
          },
        ],
        organization: selectedOrganizationDetails?.data?._id,
        type: "organization-checkout",
        ...crudPayload,
      };

      const editPayload = {
        field: [baseField],
        organization: selectedOrganizationDetails?.data?._id,
        id: data?.sectionId,
      };

      if (data?.isEdit) {
        return await dynamicFieldAddEdit(baseField);
      } else {
        return await dynamicFormAddEdit(
          data?.sectionId ? editPayload : payload
        );
      }
    },
    onSuccess: (res) => {
      if (res?.status === 200 || res?.status === 201) {
        toast.success(res?.message);
        getResponse(res);
        close();
        return res;
      } else {
        toast.warning(res?.message || res?.error);
      }
      return res;
    },
    onError: (error) => {
      console.log(error);
      toast.error("Failed to save question. Please try again.");
    },
  });

  const handleRequiredChange = (e) => {
    if (values?.questionType === "terms" && !e.target.checked) {
      return;
    }
    handleChange({ target: { name: "isRequired", value: e.target.checked } });
  };

  // ✅ Custom form submission with validation
  const handleFormSubmit = (formValues) => {
    if (validateFormData()) {
      onSubmit(formValues);
    }
  };

  const renderAdvancedOptions = () => {
    return (
      <div className="space-y-4 mt-4 p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-gray-700">
            {t("Advanced Options")}
          </h4>
          <Button
            type="button"
            buttonFunction={() => setShowAdvancedOptions(false)}
            buttonIcon={"fa-solid fa-chevron-up"}
            buttonClasses="text-gray-400 hover:text-gray-600 bg-transparent"
          />
        </div>

        {values?.questionType === "input" && (
          <>
            <Select
              label={t("Allowed Input")}
              placeholder="Select allowed input type"
              dropdownData={allowedInputOptions}
              selectedValue={values?.allowedInput || ""}
              onChange={(value) =>
                handleChange({ target: { name: "allowedInput", value } })
              }
              dropdownButtonClass={
                "!rounded-md !h-10 !border-surface-05 !bg-surface-0 !text-deep-purple"
              }
            />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Input
                  label={t("Min length")}
                  inputType="number"
                  inputPlaceholder={t("Enter min length")}
                  inputName="minLength"
                  value={values?.minLength || ""}
                  onChange={handleChange}
                  inputClasses={
                    "!border-surface-05 !bg-surface-0 !text-deep-purple"
                  }
                />
              </div>

              <div>
                <Input
                  label={t("Max length")}
                  inputType="number"
                  inputPlaceholder={t("Enter max length")}
                  inputName="maxLength"
                  value={values?.maxLength || ""}
                  onChange={handleChange}
                  inputClasses={
                    "!border-surface-05 !bg-surface-0 !text-deep-purple"
                  }
                />
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-2xl mx-auto p-2 md:p-6 bg-white rounded-lg shadow-sm">
      <div className="mb-4">
        <LangSwitch
          langs={["en", "es"]}
          selectedLang={selectedLang}
          setSelectedLang={setSelectedLang}
        />
      </div>

      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {t(data?.isEdit ? "Edit custom question" : "Add custom question")}
        </h2>
        <p className="text-sm text-gray-600">
          {t("Create a custom question that will be asked during checkout")}.
        </p>
      </div>

      <Form onSubmit={handleFormSubmit} handleSubmit={handleSubmit}>
        <div className="space-y-6">
          {formFor === "attendee" && (
            <div className="space-y-1">
              <span className="text-xs font-medium text-text-disabled">
                {t("Apply to")}...
              </span>
              <div className="flex gap-4">
                {apply_to_options?.map((opt) => (
                  <Radio
                    radioName={"fieldFor"}
                    value={opt?.value}
                    key={opt?.value}
                    radioLabel={opt?.label}
                    checked={opt?.value === values?.fieldFor}
                    onChange={(e) => {
                      handleChange({
                        target: {
                          name: e.target.name,
                          value: e.target.value,
                        },
                      });
                    }}
                  />
                ))}
              </div>
            </div>
          )}
          <div>
            <Select
              label={t("What kind of response do you want?")}
              placeholder={t("Select response type")}
              dropdownData={questionTypes}
              selectedValue={values?.questionType || ""}
              onChange={handleQuestionTypeChange}
              {...formValidate(errors, "questionType")}
              dropdownButtonClass={
                "!rounded-md !h-10 !border-surface-05 !bg-surface-04 !text-deep-purple"
              }
            />
          </div>

          <div>
            <Input
              label={t("What question do you want to ask?")}
              inputName="questionText"
              value={values?.questionText || ""}
              onChange={handleQuestionTextChange} // Use the custom handler
              placeholder={t("Enter your question here")}
              {...formValidate(errors, "questionText")}
              inputClasses={
                "!border-surface-05 !bg-surface-04 !text-deep-purple"
              }
            />

            {/* Static Label Warning Message */}
            {staticLabelWarning && (
              <div className="text-xs text-red-600 mt-1 flex items-center">
                <i className="fa-solid fa-exclamation-triangle text-sm mr-2"></i>
                {staticLabelWarning}
              </div>
            )}

            {/* Help text */}
            <div className="text-xs text-gray-500 mt-1">
              <i className="fa-solid fa-info-circle mr-1"></i>
              {t(
                `Avoid using reserved terms like "first name", "last name", "full name", or "email"`
              )}
            </div>
          </div>

          {/* Terms and Conditions Content Editor */}
          {values?.questionType === "terms" && (
            <div>
              <Editor
                label={t("Terms and Conditions Content")}
                height={200}
                required
                value={values?.overView}
                onChange={handleEditorChange}
              />
              {errors?.overView && (
                <div className="text-xs text-amber-600 mt-1">
                  <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
                  {errors.overView.message}
                </div>
              )}

              {/* Show auto-generated options preview */}
              {values?.optionsArray?.length > 0 && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-sm font-medium text-blue-800 mb-2">
                    {t("Auto-generated checkbox options")}:
                  </p>
                  <div className="space-y-1">
                    {values.optionsArray.map((option, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input type="checkbox" disabled className="w-4 h-4" />
                        <span className="text-sm text-blue-700">
                          {option.label}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    {t(
                      "Each paragraph in your content becomes a checkbox option"
                    )}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Options for select, radio, checkbox (but not terms) */}
          {["select", "radio", "checkbox"].includes(values?.questionType) &&
            values?.questionType !== "terms" && (
              <div>
                <div className="space-y-2">
                  <Input
                    label={t("Options for answer")}
                    required
                    inputPlaceholder={t("Type option and press Enter")}
                    value={currentOption}
                    onChange={(e) => setCurrentOption(e.target.value)}
                    onKeyDown={handleOptionKeyDown}
                  />

                  {/* Display validation error for options */}
                  {errors?.optionsArray && (
                    <div className="text-xs text-amber-600 mt-1">
                      <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
                      {errors.optionsArray.message}
                    </div>
                  )}

                  {/* Display added options */}
                  {values?.optionsArray?.length > 0 && (
                    <div className="space-y-2 mt-3">
                      <p className="text-sm text-gray-600">
                        {t("Added options")}:
                      </p>
                      {values.optionsArray.map((option, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between bg-gray-50 p-2 rounded border"
                        >
                          <span className="text-sm">{option?.label}</span>
                          <Button
                            type="button"
                            buttonFunction={() => removeOption(index)}
                            buttonClasses="text-red-500 hover:text-red-700 bg-transparent !px-0"
                            buttonIcon={"fa-solid fa-trash text-xs"}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

          <div className="flex items-center gap-2">
            <Checkbox
              checkboxName="isRequired"
              checked={values?.isRequired || false}
              onChange={handleRequiredChange}
              disabled={values?.questionType === "terms"}
            />
            <label className="text-sm text-gray-700">{t("Required")}</label>
            {values?.questionType === "terms" && (
              <span className="text-xs text-gray-500">
                {t("(Auto-enabled for terms)")}
              </span>
            )}
            <div className="group relative">
              <i className="fa-solid fa-info-circle text-primary text-sm cursor-help"></i>
              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-10">
                {t("Check this to make the question required")}
              </div>
            </div>
          </div>

          {values?.questionType === "input" && (
            <div>
              {!showAdvancedOptions ? (
                <button
                  type="button"
                  onClick={() => setShowAdvancedOptions(true)}
                  className="flex items-center gap-2 text-sm text-slate-600 hover:text-primary transition-colors font-medium"
                  disabled={values?.questionType !== "input"}
                >
                  <i className="fa-solid fa-chevron-down text-xs"></i>
                  {t("Advanced Options")}
                  {values?.questionType !== "input" && (
                    <span className="text-gray-400 ml-2">
                      {t("(Text fields only)")}
                    </span>
                  )}
                </button>
              ) : (
                renderAdvancedOptions()
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button
              buttonLabel={t("Cancel")}
              variant="outline"
              buttonFunction={close}
              buttonClasses="px-3 md:px-6 py-2 !border-deep-purple"
              buttonLabelClasses={"text-deep-purple"}
            />
            <Button
              buttonType="submit"
              buttonLabel={t(
                data?.isEdit ? "Update question" : "Save custom question"
              )}
              variant="primary"
              {...((isLoading || isFetching || formDetailsLoading) && {
                loading: true,
                isDisable: true,
              })}
              buttonClasses={"px-3 md:px-6"}
            />
          </div>
        </div>
      </Form>
    </div>
  );
};

export default AddBuyerQuestion;
